import { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';

const useRealtimeSync = (initialOffset = 0) => {
  const [offset, setOffset] = useState(initialOffset);
  const [connectedClients, setConnectedClients] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const isUpdatingFromRemote = useRef(false);

  useEffect(() => {
    // Initialize socket connection
    const socket = io(window.location.origin, {
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Connected to real-time sync server');
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from real-time sync server');
      setIsConnected(false);
    });

    socket.on('offset-sync', (data) => {
      console.log('Received offset sync:', data);

      // Validate the received data
      if (data && typeof data.offset === 'number') {
        // Set flag to prevent infinite loop
        isUpdatingFromRemote.current = true;
        setOffset(data.offset);

        // Reset flag after state update
        setTimeout(() => {
          isUpdatingFromRemote.current = false;
        }, 100);
      } else {
        console.warn('Invalid offset data received:', data);
      }
    });

    socket.on('clients-count', (data) => {
      setConnectedClients(data.count);
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, []);

  // Function to update offset locally and broadcast to others
  const updateOffset = (newOffset) => {
    // Don't broadcast if this update came from remote
    if (isUpdatingFromRemote.current) {
      return;
    }

    // Validate the new offset value
    if (typeof newOffset !== 'number' || isNaN(newOffset)) {
      console.warn('Invalid offset value:', newOffset);
      return;
    }

    console.log('Broadcasting offset update:', newOffset);

    // Update local state
    setOffset(newOffset);

    // Broadcast to other clients
    if (socketRef.current && isConnected) {
      socketRef.current.emit('offset-update', {
        offset: newOffset,
        timestamp: new Date().toISOString(),
      });
    }
  };

  return {
    offset,
    updateOffset,
    connectedClients,
    isConnected,
    // For cases where you need to set offset without broadcasting (like loading from localStorage)
    setOffsetSilent: setOffset,
  };
};

export default useRealtimeSync;
